import { useState, type ClipboardEvent } from 'react';
import { ActionIcon, Anchor, Badge, Button, Checkbox, FileButton, Group, Image, Paper, SimpleGrid, Text, Tooltip } from '@mantine/core';
import { IconBlur, IconEye, IconEyeOff, IconPhotoPlus, IconTrash, IconX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../lib/api';
import { deadlineProofUrl } from '../../lib/api/domains/customerDeadlines';
import type { DeadlineProof } from '../../lib/customerDeadlines';

/** Noch nicht hochgeladener Beleg im Dialog — geht beim Speichern raus. */
export interface PendingProof {
  key: string;
  file: File;
  previewUrl: string;
  visibleToCustomer: boolean;
}

export const PROOF_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/** Bilder aus einem Einfüge-Ereignis (⌘V / Strg+V). Eingefügter Text bleibt unberührt. */
export function imagesFromClipboard(event: ClipboardEvent): File[] {
  return Array.from(event.clipboardData?.items ?? [])
    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
}

export function toPendingProofs(files: File[]): PendingProof[] {
  return files
    .filter((file) => file.type.startsWith('image/'))
    .map((file, i) => ({
      key: `${Date.now()}-${i}-${file.name}`,
      file,
      previewUrl: URL.createObjectURL(file),
      visibleToCustomer: false,
    }));
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('Bild konnte nicht gelesen werden'));
    reader.readAsDataURL(file);
  });
}

/**
 * Belege eines Postens: vorhandene (mit Freigabe und Entfernen) und frisch eingefügte,
 * die beim Speichern hochgeladen werden. Einfügen funktioniert im ganzen Dialog — der
 * Aufrufer hängt `imagesFromClipboard` an sein onPaste. Neue Bilder gehen über
 * `onAddImages` erst in den Verpixeln-Editor, bevor sie hier als ausstehend erscheinen.
 */
export function DeadlineProofsField({
  deadlineId,
  proofs,
  pending,
  onPendingChange,
  onAddImages,
  onRedact,
  onChanged,
}: {
  /** null = Posten wird gerade angelegt */
  deadlineId: string | null;
  proofs: DeadlineProof[];
  pending: PendingProof[];
  onPendingChange: (next: PendingProof[]) => void;
  /** Gewählte Bilder — gehen zuerst in den Verpixeln-Editor */
  onAddImages: (files: File[]) => void;
  /** Ausstehenden Beleg nachträglich verpixeln */
  onRedact: (item: PendingProof) => void;
  /** Nach Freigabe/Entfernen — Liste neu laden */
  onChanged: () => void;
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: (proof: DeadlineProof) =>
      api.updateDeadlineProof(deadlineId!, proof.id, { visibleToCustomer: !proof.visibleToCustomer }),
    onSuccess: () => onChanged(),
    onError: (e: Error) => notifications.show({ title: 'Freigabe nicht geändert', message: e.message, color: 'red' }),
  });

  const remove = useMutation({
    mutationFn: (proofId: string) => api.deleteDeadlineProof(deadlineId!, proofId),
    onSuccess: () => {
      setConfirmId(null);
      onChanged();
    },
    onError: (e: Error) => notifications.show({ title: 'Beleg nicht entfernt', message: e.message, color: 'red' }),
  });

  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Text size="sm" fw={500}>
          Belege
        </Text>
        <Text size="xs" c="dimmed">
          nur Bilder · max. 8 MB
        </Text>
      </Group>
      <Paper
        withBorder
        radius="md"
        p="sm"
        style={{ borderStyle: 'dashed', backgroundColor: 'var(--mantine-color-default-hover)' }}
      >
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <IconPhotoPlus size={22} style={{ color: 'var(--mantine-color-dimmed)', flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <Text size="sm">Screenshot mit ⌘V / Strg+V einfügen</Text>
              <Text size="xs" c="dimmed">
                Danach Einkaufspreise, Kontodaten und Namen mit der Maus verpixeln oder schwärzen — gespeichert
                wird nur das bearbeitete Bild. Neue Belege sind nur intern sichtbar, bis du sie freigibst.
              </Text>
            </div>
          </Group>
          <FileButton
            multiple
            accept={PROOF_ACCEPT}
            onChange={(files) => {
              const images = files.filter((file) => file.type.startsWith('image/'));
              if (images.length > 0) onAddImages(images);
            }}
          >
            {(props) => (
              <Button {...props} size="compact-sm" variant="light" style={{ flexShrink: 0 }}>
                Datei wählen
              </Button>
            )}
          </FileButton>
        </Group>
      </Paper>

      {(proofs.length > 0 || pending.length > 0) && (
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs" mt="xs">
          {deadlineId &&
            proofs.map((proof) => {
              const url = deadlineProofUrl(deadlineId, proof.id);
              return (
                <Paper key={proof.id} withBorder radius="sm" p={6}>
                  <Anchor href={url} target="_blank" rel="noopener noreferrer" title={proof.filename}>
                    <Image src={url} h={90} fit="cover" radius="xs" alt={proof.filename} />
                  </Anchor>
                  <Group justify="space-between" gap={4} mt={6} wrap="nowrap">
                    <Badge size="xs" variant="light" color={proof.visibleToCustomer ? 'teal' : 'gray'}>
                      {proof.visibleToCustomer ? 'im Portal' : 'intern'}
                    </Badge>
                    <Group gap={2} wrap="nowrap">
                      <Tooltip label={proof.visibleToCustomer ? 'Im Kundenportal ausblenden' : 'Im Kundenportal zeigen'}>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="gray"
                          aria-label={proof.visibleToCustomer ? 'Im Kundenportal ausblenden' : 'Im Kundenportal zeigen'}
                          loading={toggle.isPending && toggle.variables?.id === proof.id}
                          onClick={() => toggle.mutate(proof)}
                        >
                          {proof.visibleToCustomer ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                        </ActionIcon>
                      </Tooltip>
                      {confirmId === proof.id ? (
                        <Button
                          size="compact-xs"
                          color="red"
                          loading={remove.isPending}
                          onClick={() => remove.mutate(proof.id)}
                        >
                          Entfernen
                        </Button>
                      ) : (
                        <Tooltip label="Beleg entfernen">
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            aria-label={`${proof.filename} entfernen`}
                            onClick={() => setConfirmId(proof.id)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Group>
                </Paper>
              );
            })}
          {pending.map((item) => (
            <Paper key={item.key} withBorder radius="sm" p={6} style={{ borderStyle: 'dashed' }}>
              <Image src={item.previewUrl} h={90} fit="cover" radius="xs" alt={item.file.name} />
              <Group justify="space-between" gap={4} mt={6} wrap="nowrap">
                <Checkbox
                  size="xs"
                  label="Im Portal"
                  checked={item.visibleToCustomer}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    onPendingChange(pending.map((p) => (p.key === item.key ? { ...p, visibleToCustomer: checked } : p)));
                  }}
                />
                <Group gap={2} wrap="nowrap">
                  <Tooltip label="Bereiche verpixeln oder schwärzen">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="gray"
                      aria-label={`${item.file.name} verpixeln`}
                      onClick={() => onRedact(item)}
                    >
                      <IconBlur size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    aria-label="Nicht hochladen"
                    onClick={() => {
                      URL.revokeObjectURL(item.previewUrl);
                      onPendingChange(pending.filter((p) => p.key !== item.key));
                    }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              </Group>
              <Text size="xs" c="dimmed" mt={2}>
                wird beim Speichern hochgeladen
              </Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}
    </div>
  );
}
